/**
 * MedicareStickyCta — high-intent CTA pill that floats above the
 * viewport once the visitor has scrolled past the 3rd Medicare post.
 *
 * Why a sticky pill (vs. an inline CTA banner): Medicare leads convert
 * roughly 3× higher when the call-to-book is in the visual viewport at
 * the decision moment, not at the bottom of the article. We don't
 * surface it immediately — it appears only AFTER engagement (the
 * visitor has read at least three post summaries) so it reads as a
 * helpful next step, not a pop-up.
 *
 * Rendering rules:
 * - Mounts only on /blog/category/health (parent guards).
 * - Reveals when an IntersectionObserver sentinel placed under the 3rd
 *   <li> intersects the viewport.
 * - Dismissible — dismissal persists in sessionStorage so the CTA
 *   doesn't fight the user on the rest of the visit. We use
 *   sessionStorage (not localStorage) so a returning user gets a fresh
 *   chance on the next session.
 * - Two affordances: (1) `tel:` link to the agency phone, (2) a button
 *   that opens the existing ContactDialog with a Medicare-flagged
 *   default message so the inbound lead is auto-categorised.
 */

import React, { useEffect, useState } from "react";
import { Phone, X, MessageCircle } from "lucide-react";
import ContactDialog from "@/components/ContactDialog";
import { COMPANY } from "@/lib/compliance";

const DISMISS_KEY = "encc:medicare-cta:dismissed";

export function MedicareCtaSentinel({ setNode }) {
    // Empty <div> sandwich placed by the parent after the 3rd post.
    // Its intersection event is what flips the CTA visible. We use a
    // callback ref (not a useRef) so the parent re-renders the
    // MedicareStickyCta with the live DOM node — IntersectionObserver
    // can then be attached after async post-fetch completes.
    return (
        <div
            ref={setNode}
            data-testid="medicare-cta-sentinel"
            aria-hidden="true"
            className="h-px w-full"
        />
    );
}

export default function MedicareStickyCta({ sentinelNode }) {
    const [revealed, setRevealed] = useState(false);
    const [dismissed, setDismissed] = useState(() => {
        if (typeof window === "undefined") return false;
        try {
            return window.sessionStorage.getItem(DISMISS_KEY) === "1";
        } catch {
            return false;
        }
    });

    useEffect(() => {
        if (!sentinelNode || dismissed) return;
        const io = new IntersectionObserver(
            (entries) => {
                for (const e of entries) {
                    if (e.isIntersecting) {
                        setRevealed(true);
                        // One-shot — once revealed we leave it on.
                        io.disconnect();
                        break;
                    }
                }
            },
            { rootMargin: "0px 0px -10% 0px", threshold: 0.01 }
        );
        io.observe(sentinelNode);
        return () => io.disconnect();
    }, [sentinelNode, dismissed]);

    const handleDismiss = () => {
        setDismissed(true);
        try {
            window.sessionStorage.setItem(DISMISS_KEY, "1");
        } catch {
            /* sessionStorage unavailable (private mode) — ignore */
        }
    };

    if (dismissed || !revealed) return null;

    return (
        <div
            data-testid="medicare-sticky-cta"
            role="region"
            aria-label="Talk to a Medicare-licensed agent"
            className="pointer-events-none fixed inset-x-0 bottom-4 z-50 flex justify-center px-4 sm:bottom-6 sm:px-6"
        >
            <div
                className="pointer-events-auto flex w-full max-w-xl items-stretch overflow-hidden rounded-full border border-[rgba(212,175,55,0.55)] bg-[#0b0e13]/95 shadow-[0_18px_40px_-12px_rgba(0,0,0,0.65)] backdrop-blur supports-[backdrop-filter]:bg-[#0b0e13]/85 sm:w-auto"
                style={{
                    animation: "encc-cta-rise 280ms cubic-bezier(0.16,1,0.3,1) both",
                }}
            >
                {/* Identity / framing copy. Hidden on very narrow screens
                    to keep the pill thumb-friendly. */}
                <div className="hidden items-center gap-2 pl-5 pr-3 text-left sm:flex">
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-brand-gold/50 text-brand-gold2">
                        <MessageCircle aria-hidden="true" className="h-3.5 w-3.5" />
                    </span>
                    <span className="whitespace-nowrap text-[11px] uppercase tracking-[0.28em] text-brand-goldSoft">
                        Talk to a Medicare-licensed agent
                    </span>
                </div>

                <a
                    data-testid="medicare-sticky-cta-call"
                    href={`tel:${COMPANY.phoneTel}`}
                    className="flex flex-1 items-center justify-center gap-2 border-l border-[rgba(212,175,55,0.25)] px-4 py-3 text-sm font-medium text-brand-ivory transition-colors hover:bg-[rgba(212,175,55,0.08)] focus:outline-none focus-visible:bg-[rgba(212,175,55,0.12)] focus-visible:ring-2 focus-visible:ring-brand-gold sm:flex-none sm:px-5"
                >
                    <Phone aria-hidden="true" className="h-4 w-4 text-brand-gold2" />
                    <span className="whitespace-nowrap">
                        <span className="sm:hidden">Call</span>
                        <span className="hidden sm:inline">
                            Call {COMPANY.phone}
                        </span>
                    </span>
                </a>

                <ContactDialog
                    defaultMessage="I have a Medicare question: "
                    trigger={
                        <button
                            type="button"
                            data-testid="medicare-sticky-cta-ask"
                            className="flex flex-1 items-center justify-center gap-2 border-l border-[rgba(212,175,55,0.25)] bg-[rgba(212,175,55,0.12)] px-4 py-3 text-sm font-semibold uppercase tracking-[0.16em] text-brand-gold2 transition-colors hover:bg-[rgba(212,175,55,0.22)] hover:text-brand-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold sm:flex-none sm:px-5"
                        >
                            <span className="whitespace-nowrap">Ask a question</span>
                        </button>
                    }
                />

                <button
                    type="button"
                    onClick={handleDismiss}
                    data-testid="medicare-sticky-cta-dismiss"
                    aria-label="Dismiss Medicare contact CTA"
                    className="flex items-center justify-center border-l border-[rgba(212,175,55,0.25)] px-3 text-brand-muted transition-colors hover:bg-[rgba(255,255,255,0.04)] hover:text-brand-ivory focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
                >
                    <X aria-hidden="true" className="h-4 w-4" />
                </button>
            </div>

            {/* Local keyframes — keeps the entrance motion off the global
                stylesheet so it's tree-shaken with the component. */}
            <style>{`
                @keyframes encc-cta-rise {
                    from { opacity: 0; transform: translateY(14px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </div>
    );
}
